import { Fragment, useEffect, useMemo, useState } from "react";
import { Modal, DatePicker, Select, Spin, Input } from "antd";
import { useOrderingMessage } from "../../useOrderingMessage";
import {
  FileDoneOutlined,
  DeleteOutlined,
  EditOutlined,
  BarsOutlined,
  PlusOutlined,
  ExclamationCircleFilled,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import dayjs, { Dayjs } from "dayjs";
import { posModalStyles as modalStyles } from "../../modalPrimitives";
import { useViewport } from "../../../../../hooks/useViewport";
import { useDefaultCompany } from "../../../../../hooks/useCompanies";
import { useActivePaymentMethods, usePaymentMethods } from "../../../../../hooks/usePaymentMethods";
import {
  INVOICE_NUMBER_LENGTH,
  nextInvoiceNumberOf,
} from "../../../../../lib/invoiceNumbering";
import { moneyToWordsBg } from "../../../../../lib/bgNumberToWords";
import { invoiceIssuingOf, usesSharedInvoiceSeries } from "../../../../../lib/fiscalInvoice";
import { computeVatBreakdown } from "../../../../../lib/vat";
import { R } from "../../orderingTheme";
import {
  GhostButton,
  ModalFooter,
  ModalHeader,
  PrimaryButton,
} from "../../modalPrimitives";
import type { BillHistory } from "../../../../../api/reportsBillsHistory";
import type { Invoice } from "../../../../../api/invoices";
import InvoiceLineEditModal from "./InvoiceLineEditModal";
import InvoiceSummarizeModal from "./InvoiceSummarizeModal";
import { NoticeBanner } from "./NoticeBanner";
import { useInvoiceSubmit } from "./useInvoiceSubmit";
import {
  isDepositInvoiceTotalMismatch,
  useDepositInvoiceMethod,
  type DepositInvoiceSource,
} from "./depositInvoice";
import { useInvoiceLines } from "./useInvoiceLines";
import { invoicePaymentOptionsOf } from "./invoicePaymentOptions";
import { useInvoiceBuyer } from "./useInvoiceBuyer";
import { InvoiceBuyerFields } from "./InvoiceBuyerFields";
import { INVOICE_LINE_GRID_CLASS, InvoiceLineRow } from "./InvoiceLineRow";
import {
  formatExpectedBillsTotal,
  hasTipLines,
  isDepositLine,
  isInvoiceTotalMismatch,
  isLockedLine,
  toInvoiceLinePayload,
  toVatInputs,
} from "./invoiceLines";

interface Props {
  open: boolean;
  onClose: () => void;
  bills: BillHistory[];
  // Извиква се със създадената фактура — родителят отваря preview.
  onSaved?: (invoice: Invoice) => void;
  /** „Свободна фактура": без сметки, само „По сметка" и без бон
   *  (`useInvoiceSubmit.ts`). */
  free?: boolean;
  /** Фактура за депозит (с `free=false`): започва с реда „Авансово плащане
   *  (капаро)" и метода му; `POST /invoices` с `deposit_id`, без нов бон. */
  deposit?: DepositInvoiceSource | null;
}

// Артикул в инвойса — типът и цялата логика на редовете (вкл. заключения ред
// за бакшиш) са в `invoiceLines.ts`.
export type { Line } from "./invoiceLines";

/**
 * InvoiceCreateModal — стъпка 2 от Фактуриране workflow-а.
 *
 * За тази итерация: показва всички полета попълнени с правилни default-и
 * (доставчик от текущата фирма, активни методи на плащане, артикулите от
 * избраните сметки), без редакция на редовете, без save endpoint, без
 * клавиатурна навигация. Тези части ще се добавят в следващи итерации.
 */
export const InvoiceCreateModal = ({
  open,
  onClose,
  bills,
  onSaved,
  free = false,
  deposit = null,
}: Props) => {
  const { t } = useTranslation();
  const message = useOrderingMessage();
  const { isMobile, isNarrow, isTablet, isDesktop } = useViewport();

  const { data: defaultCompany, isLoading: companyLoading } = useDefaultCompany(
    {
      enabled: open,
    },
  );
  const { data: activePMs = [], isLoading: pmLoading } =
    useActivePaymentMethods();

  const invoiceSubmit = useInvoiceSubmit({ free, onSaved, depositId: deposit?.id ?? null });
  // Мастър списъкът — за метода на сметките, който го няма сред активните
  // („Смесено"; `invoicePaymentOptions.ts`).
  const { data: allPMs = [] } = usePaymentMethods();
  const paymentOptions = useMemo(
    () => invoicePaymentOptionsOf(activePMs, allPMs, bills, free),
    [activePMs, allPMs, bills, free],
  );
  // Фактура за закрити сметки с един общ метод: бонът е издаден с него —
  // изборът е заключен (`invoicePaymentOptionsOf` дава само методите на сметките).
  const paymentFromBills = !free && bills.length > 0 && paymentOptions.length === 1;

  // ── Document header ─────────────────────────────────────────────────────
  const [invoiceType] = useState<"tax" | "simplified">("tax");
  const [issueDate, setIssueDate] = useState<Dayjs>(dayjs());
  const [supplyDate, setSupplyDate] = useState<Dayjs>(dayjs());
  const [supplyPlace, setSupplyPlace] = useState("");
  const [paymentMethodId, setPaymentMethodId] = useState<number | null>(null);
  const [invoiceNumber, setInvoiceNumber] = useState<string>("");

  useEffect(() => {
    if (!open) return;
    setIssueDate(dayjs());
    setSupplyPlace("");
    // Дата на сделката = момента на закриване на сметката (bill.closed_at).
    // При няколко избрани сметки взимаме най-късното закриване — това е
    // моментът, в който фактически приключи цялата „сделка". Fallback на
    // днес само ако bills нямат closed_at (защитна стратегия — не би
    // трябвало да се случва за затворени сметки).
    const latestClosedAt = bills.reduce<number | null>((acc, b) => {
      if (!b.closed_at) return acc;
      const ts = new Date(b.closed_at).getTime();
      return acc === null || ts > acc ? ts : acc;
    }, null);
    setSupplyDate(latestClosedAt !== null ? dayjs(latestClosedAt) : dayjs());
  }, [open, bills]);

  // Общата поредица („И двете“; във „Фискално устройство“ входовете ги няма):
  // номерът се дава от BE при записа — полето показва следващия, само за
  // четене; записаният идва от отговора.
  const sharedSeries = usesSharedInvoiceSeries(invoiceIssuingOf(defaultCompany));

  // Когато идват default company / payment methods — попълваме default-ите.
  useEffect(() => {
    if (!open) return;
    if (defaultCompany) {
      // Същата формула, която рисува прегледа в „Номерация на документи“ —
      // едно място, за да не се разминат числото на екрана и на документа.
      setInvoiceNumber(nextInvoiceNumberOf(defaultCompany.last_invoice_number));
    }
  }, [open, defaultCompany]);

  useEffect(() => {
    if (!open) return;
    // Компонентът живее между отварянията: метод от предишна фактура по
    // сметки („Смесено") не е сред методите на свободната — избира се наново.
    if (
      paymentMethodId !== null &&
      paymentOptions.some((pm) => pm.id === paymentMethodId)
    )
      return;
    if (paymentOptions.length === 0) return;
    // Ако всички сметки са пуснати с един метод — него предлагаме като default.
    const uniquePmIds = Array.from(
      new Set(
        bills
          .map((b) => b.payment_method_id)
          .filter((v): v is number => v !== null && v !== undefined),
      ),
    );
    if (uniquePmIds.length === 1) {
      setPaymentMethodId(uniquePmIds[0]);
    } else {
      setPaymentMethodId(paymentOptions[0].id);
    }
  }, [open, paymentOptions, bills, paymentMethodId]);
  useDepositInvoiceMethod({ open, deposit, paymentOptions, setPaymentMethodId });

  // ── Клиент ───────────────────────────────────────────────────────────────
  // Полетата, търсенето в издадените фактури и иконата до „ЕИК" — общи с
  // фискалната фактура при закриване (`useInvoiceBuyer.ts`). „Получил" е там,
  // защото следва фирмата (свободно поле с памет от 1 име на клиент: празно
  // при save не пипа DB-то, ново непразно име презаписва предишното).
  const buyer = useInvoiceBuyer({ open });

  // ── Получаване ──────────────────────────────────────────────────────────
  const [receivedAt, setReceivedAt] = useState<Dayjs | null>(dayjs());
  useEffect(() => {
    if (open) setReceivedAt(dayjs());
  }, [open]);

  // ── Line items ──────────────────────────────────────────────────────────
  // Състоянието и действията с редовете — `useInvoiceLines.ts`.
  const {
    lines,
    selectedLineId,
    setSelectedLineId,
    summarizeMode,
    patchLine,
    removeLine,
    addLine,
    applySummarizeMode,
  } = useInvoiceLines({ open, free, deposit, bills });

  // ── Totals ──────────────────────────────────────────────────────────────
  // 5 стойности съответстващи на оригиналния POS формуляр:
  // - Облагаема сума: net на редовете с ДДС > 0
  // - Необлагаема сума: total на редовете с ДДС = 0
  // - ДДС: сума на ДДС от облагаемите редове
  // - Всичко сума: Облагаема + Необлагаема (преди ДДС)
  // - Обща стойност: Всичко сума + ДДС
  // Цялата ДДС разбивка (нето/ДДС/бруто на ред + груповите тотали) минава през
  // един споделен алгоритъм (lib/vat.ts), идентичен 1:1 с бекенда. Той гарантира,
  // че сумата на редовете ВИНАГИ е равна на Облагаема/ДДС/Обща стойност.
  const breakdown = useMemo(
    () => computeVatBreakdown(toVatInputs(lines)),
    [lines],
  );

  // ── Забележка ───────────────────────────────────────────────────────────
  // Празна при всяко отваряне: препратката към фискалния бон на сметките
  // („Към фискален бон № N от Д, ФУ S") я слага BE при записа
  // (`invoices/receipt_refs.py`); номерът на сметката не е нужен на фактурата.
  const [note, setNote] = useState("");
  useEffect(() => {
    if (open) setNote("");
  }, [open, bills]);

  // ── Item action modals ──────────────────────────────────────────────────
  const [editLineId, setEditLineId] = useState<string | null>(null);
  const [deleteLineId, setDeleteLineId] = useState<string | null>(null);
  const [summarizeOpen, setSummarizeOpen] = useState(false);
  const [newLineOpen, setNewLineOpen] = useState(false);
  // Свободната фактура започва празна — първото действие е редът („Чадъри — 60");
  // тази за депозит вече има своя ред.
  useEffect(() => {
    if (open && free && !deposit) setNewLineOpen(true);
  }, [open, free, deposit]);
  const [mismatchConfirmOpen, setMismatchConfirmOpen] = useState(false);

  // Сума на закритите сметки (поръчано + бакшиш, в центове) — против това
  // сравняваме totals.grand при save, за да предпазим оператора от случайно
  // издаване на фактура с различна сума от платените сметки.
  const billsTotalLabel = useMemo(() => formatExpectedBillsTotal(bills), [bills]);

  // Свободната фактура и тази за депозит нямат сметки, срещу които да се сравни.
  const totalsMismatch =
    !free && !deposit && isInvoiceTotalMismatch(bills, breakdown.grandTotal);

  // ── FE validation преди save ────────────────────────────────────────────
  // Per-field error map — позволява да маркираме точно полето в червено
  // (status="error" на antd) при клик на Запис. Showing-ът се пуска само
  // след първи опит за save, за да не „крещи" модалът при отваряне.
  const [showErrors, setShowErrors] = useState(false);
  useEffect(() => {
    if (!open) setShowErrors(false);
  }, [open]);

  const fieldErrors = useMemo((): Record<string, string> => {
    const e: Record<string, string> = {};
    if (!invoiceNumber.trim() || !/^\d+$/.test(invoiceNumber)) {
      e.invoice_number = t("invoiceCreate.errInvoiceNumber");
    }
    if (!issueDate) e.issue_date = t("invoiceCreate.errDates");
    if (!supplyDate) e.supply_date = t("invoiceCreate.errDates");
    if (paymentMethodId == null) {
      e.payment_method = t("invoiceCreate.errPaymentMethod");
    }
    Object.assign(e, buyer.errors);
    if (lines.length === 0) {
      e.lines = t("invoiceCreate.errNoLines");
    }
    return e;
  }, [
    invoiceNumber,
    issueDate,
    supplyDate,
    paymentMethodId,
    buyer.errors,
    lines.length,
    t,
  ]);

  // Връща статус за дадено поле — undefined ако още не сме опитали save
  // или ако полето е валидно. „error" иначе → antd го рисува в червено.
  const errStatus = (key: string) =>
    showErrors && fieldErrors[key] ? "error" : undefined;

  const performSave = async () => {
    // Защита от двойно натискане (и „Да" в диалога за разминаване) —
    // вторият POST би бил със същия номер и би върнал 409.
    if (invoiceSubmit.isPending) return;
    setMismatchConfirmOpen(false);
    await invoiceSubmit.submit({
      invoice_number: invoiceNumber.trim().padStart(INVOICE_NUMBER_LENGTH, "0"),
      invoice_type: invoiceType,
      issue_date: issueDate.format("YYYY-MM-DD"),
      supply_date: supplyDate.format("YYYY-MM-DD"),
      supply_place: supplyPlace.trim() || null,
      payment_method_id: paymentMethodId,
      customer: buyer.toCustomerPayload(),
      received_by: buyer.receivedBy.trim() || null,
      received_at: receivedAt ? receivedAt.format("YYYY-MM-DD") : null,
      note: note.trim() || null,
      bill_ids: bills.map((b) => b.id),
      lines: lines.map(toInvoiceLinePayload),
    });
  };

  const handleSaveClick = () => {
    if (invoiceSubmit.isPending) return;
    setShowErrors(true);
    const firstError = Object.values(fieldErrors)[0];
    if (firstError) {
      message.warning(firstError);
      return;
    }
    // Фактурата за депозит е точно за сумата му (BE я отказва иначе).
    if (deposit && isDepositInvoiceTotalMismatch(deposit, breakdown.grandTotal)) {
      message.warning(t("depositInvoice.errTotal", { amount: deposit.amount }));
      return;
    }
    if (totalsMismatch) {
      setMismatchConfirmOpen(true);
      return;
    }
    void performSave();
  };

  const selectedLine = lines.find((l) => l.id === selectedLineId) ?? null;
  // Редовете за бакшиш и за депозит са записани при закриването — не се
  // променят и не се трият.
  const selectedLineLocked = selectedLine !== null && isLockedLine(selectedLine);
  const invoiceHasTipLines = hasTipLines(lines);
  const invoiceHasDepositLines = lines.some(isDepositLine);
  const editLine = lines.find((l) => l.id === editLineId) ?? null;
  const deleteLine = lines.find((l) => l.id === deleteLineId) ?? null;

  // ── Modal sizing ────────────────────────────────────────────────────────
  const modalWidth = isMobile ? undefined : isTablet ? 880 : 1180;

  const loading = companyLoading || pmLoading;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      closable={false}
      destroyOnHidden
      width={modalWidth}
      styles={modalStyles}
      style={{ maxWidth: "calc(100vw - 32px)" }}
    >
      <div
        className="inv-create"
        style={{
          background: R.surface,
          display: "flex",
          flexDirection: "column",
          maxHeight: "92vh",
        }}
      >
        <style>{`
          .inv-create .ant-input,
          .inv-create .ant-input-affix-wrapper,
          .inv-create .ant-input-number,
          .inv-create .ant-select .ant-select-selector,
          .inv-create .ant-picker {
            border-radius: 10px !important;
            border-color: ${R.border} !important;
            background: ${R.surface} !important;
            min-height: 38px !important;
            font-size: 13px;
            color: ${R.ink};
          }
          .inv-create .ant-input { height: 38px !important; padding: 4px 12px !important; }
          /* Самият Select (32px на antd) — колкото полето в него, иначе то
             стърчи 6px под реда и решетката (align-items: end) го разминава. */
          .inv-create .ant-select-single { height: 38px !important; }
          /* Когато Input има allowClear → той се wrap-ва в ant-input-affix-wrapper
             с вътрешен padding + .ant-input на 38px → реалната височина става ~46px.
             Фиксираме wrapper-а на 38px и нулираме inner-а, за да съвпада визуално
             с останалите полета (DatePicker, Select). */
          .inv-create .ant-input-affix-wrapper {
            height: 38px !important;
            padding: 0 11px !important;
          }
          .inv-create .ant-input-affix-wrapper > .ant-input {
            height: auto !important;
            padding: 0 !important;
            border: 0 !important;
            background: transparent !important;
            box-shadow: none !important;
          }
          .inv-create .ant-input:hover,
          .inv-create .ant-input:focus,
          .inv-create .ant-select:hover .ant-select-selector,
          .inv-create .ant-select-focused .ant-select-selector,
          .inv-create .ant-picker:hover,
          .inv-create .ant-picker-focused,
          .inv-create .ant-input-number:hover,
          .inv-create .ant-input-number-focused {
            border-color: ${R.brand} !important;
            box-shadow: 0 0 0 3px color-mix(in srgb, var(--color-brand) 12%, transparent) !important;
          }
          .inv-create .ant-input::placeholder { color: ${R.textMuted} !important; }

          /* Грешка → antd-та използва тези класове при status="error".
             Понеже горните общи правила имат !important, трябва да го override-нем,
             за да реално светне червено. */
          .inv-create .ant-input-status-error,
          .inv-create .ant-input-affix-wrapper-status-error,
          .inv-create .ant-input-number-status-error,
          .inv-create .ant-select-status-error .ant-select-selector,
          .inv-create .ant-picker-status-error {
            border-color: ${R.danger} !important;
            box-shadow: 0 0 0 3px rgba(220,38,38,0.12) !important;
            background: var(--color-danger-tint) !important;
          }
        `}</style>

        <ModalHeader
          icon={<FileDoneOutlined />}
          title={free ? t("freeInvoice.title") : t("invoiceCreate.title")}
          subtitle={
            free
              ? t("freeInvoice.subtitle")
              : deposit
                ? t("depositInvoice.subtitle", { id: deposit.id })
                : t("invoiceCreate.subtitle", { count: bills.length })
          }
          onClose={onClose}
          isNarrow={isNarrow}
        />

        {loading ? (
          <div
            style={{ padding: 60, display: "flex", justifyContent: "center" }}
          >
            <Spin />
          </div>
        ) : (
          <div
            style={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              padding: isNarrow ? "14px 16px" : "14px 20px",
              background: R.bg,
              display: "flex",
              flexDirection: "column",
              gap: 10,
            }}
          >
            {/* Свободната фактура е само „По сметка" — казват го изборът
                („Фактуриране") и заключеното поле; лента за това няма (място). */}
            {deposit ? <NoticeBanner tone="info" text={t("depositInvoice.notice")} /> : null}

            {/* Документ — на цяла ширина, с получаването (до 0.0.389 — своя
                секция). На десктоп 4 колони: два реда, за да се събира
                фактурата на един екран. */}
            <Section title={t("invoiceCreate.secDocument")}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isNarrow
                    ? "1fr"
                    : isDesktop
                      ? "repeat(4, minmax(0, 1fr))"
                      : "1fr 1fr",
                  gap: "8px 10px",
                  // Етикет с подсказка може да е на два реда (тесен десктоп) —
                  // полетата на реда остават на една линия.
                  alignItems: "end",
                }}
              >
                <Field
                  label={t("invoiceCreate.fieldNumber")}
                  required
                  hint={
                    sharedSeries
                      ? t("fiscalInvoice.software.numberAuto")
                      : t("invoiceCreate.numberHint")
                  }
                >
                  <Input
                    status={errStatus("invoice_number")}
                    value={invoiceNumber}
                    readOnly={sharedSeries}
                    onChange={(e) =>
                      setInvoiceNumber(
                        e.target.value.replace(/\D/g, "").slice(0, 10),
                      )
                    }
                  />
                </Field>
                <Field label={t("invoiceCreate.fieldType")}>
                  <Select
                    value={invoiceType}
                    style={{ width: "100%" }}
                    options={[
                      { value: "tax", label: t("invoiceCreate.typeTax") },
                      {
                        value: "simplified",
                        label: t("invoiceCreate.typeSimplified"),
                      },
                    ]}
                  />
                </Field>
                <Field
                  label={t("invoiceCreate.fieldPaymentMethod")}
                  hint={
                    free
                      ? t("freeInvoice.paymentHint")
                      : paymentFromBills
                        ? t("invoiceCreate.paymentFromBills")
                        : undefined
                  }
                  required
                >
                  <Select
                    status={errStatus("payment_method")}
                    value={paymentMethodId ?? undefined}
                    style={{ width: "100%" }}
                    onChange={setPaymentMethodId}
                    disabled={free || paymentFromBills}
                    options={paymentOptions.map((pm) => ({
                      value: pm.id,
                      label: pm.name_bg,
                    }))}
                  />
                </Field>
                <Field label={t("invoiceCreate.fieldIssueDate")} required>
                  <DatePicker
                    status={errStatus("issue_date")}
                    value={issueDate}
                    onChange={(d) => d && setIssueDate(d)}
                    format="DD.MM.YYYY"
                    allowClear={false}
                    style={{ width: "100%" }}
                  />
                </Field>
                <Field label={t("invoiceCreate.fieldSupplyDate")} required>
                  <DatePicker
                    status={errStatus("supply_date")}
                    value={supplyDate}
                    onChange={(d) => d && setSupplyDate(d)}
                    format="DD.MM.YYYY"
                    allowClear={false}
                    style={{ width: "100%" }}
                  />
                </Field>
                <Field
                  label={t("invoiceCreate.fieldSupplyPlace")}
                  hint={t("invoiceCreate.optional")}
                >
                  <Input
                    value={supplyPlace}
                    onChange={(e) => setSupplyPlace(e.target.value)}
                  />
                </Field>
                <Field label={t("invoiceCreate.fieldReceivedBy")}>
                  <Input
                    value={buyer.receivedBy}
                    maxLength={255}
                    onChange={(e) => buyer.setReceivedBy(e.target.value)}
                    placeholder={t("invoiceCreate.fieldReceivedByPh")}
                    allowClear
                  />
                </Field>
                <Field label={t("invoiceCreate.fieldReceivedAt")}>
                  <DatePicker
                    value={receivedAt}
                    onChange={(d) => setReceivedAt(d)}
                    format="DD.MM.YYYY"
                    style={{ width: "100%" }}
                  />
                </Field>
              </div>
            </Section>

            {/* Клиент — на цяла ширина. На най-широки екрани (isDesktop) два
                  реда по четири (`InvoiceBuyerFields`, `document`). */}
            <Section title={t("invoiceCreate.secCustomer")}>
              <InvoiceBuyerFields buyer={buyer} errStatus={errStatus} layout="document" />
            </Section>

            {/* Артикули */}
            <Section
              title={t("invoiceCreate.secItems")}
              action={
                <div
                  style={{
                    display: "grid",
                    // На narrow → 2×2 grid, на широки → row (1×4) с auto-fit.
                    gridTemplateColumns: isNarrow
                      ? "1fr 1fr"
                      : "repeat(4, auto)",
                    gap: 6,
                  }}
                >
                  <SectionAction
                    icon={<BarsOutlined />}
                    label={t("invoiceCreate.actionSummarize")}
                    disabled={lines.length === 0 && summarizeMode === "per-item"}
                    onClick={() => setSummarizeOpen(true)}
                  />
                  <SectionAction
                    icon={<PlusOutlined />}
                    label={t("invoiceCreate.actionAdd")}
                    onClick={() => setNewLineOpen(true)}
                  />
                  <SectionAction
                    icon={<EditOutlined />}
                    label={t("invoiceCreate.actionEdit")}
                    disabled={!selectedLine || selectedLineLocked}
                    onClick={() =>
                      selectedLine && !selectedLineLocked && setEditLineId(selectedLine.id)
                    }
                  />
                  <SectionAction
                    icon={<DeleteOutlined />}
                    label={t("invoiceCreate.actionDelete")}
                    disabled={!selectedLine || selectedLineLocked}
                    onClick={() =>
                      selectedLine && !selectedLineLocked && setDeleteLineId(selectedLine.id)
                    }
                  />
                </div>
              }
            >
              <div
                style={{
                  background: R.surface,
                  border: `1px solid ${R.border}`,
                  borderRadius: 10,
                  overflow: "hidden",
                }}
              >
                {/* Header-ът седи ВЪТРЕ в scroll container-а със
                    `position: sticky` — иначе при появяване на
                    вертикален scrollbar 1fr колоната на body-то се свива и
                    последните pixel-колони „избягат" вляво спрямо header-а.
                    Така header и rows винаги имат еднакъв „1fr" размер. */}
                <div style={{ maxHeight: 360, overflow: "auto" }}>
                  <div
                    className={INVOICE_LINE_GRID_CLASS}
                    style={{
                      padding: "10px 12px",
                      background: R.bg,
                      borderBottom: `1px solid ${R.border}`,
                      fontSize: 10.5,
                      fontWeight: 700,
                      color: R.textMuted,
                      letterSpacing: 0.8,
                      textTransform: "uppercase",
                      position: "sticky",
                      top: 0,
                      zIndex: 1,
                    }}
                  >
                    <span>#</span>
                    <span>{t("invoiceCreate.colName")}</span>
                    <span>{t("invoiceCreate.colUnit")}</span>
                    <span>{t("invoiceCreate.colVat")}</span>
                    <span style={{ textAlign: "right" }}>
                      {t("invoiceCreate.colQty")}
                    </span>
                    <span style={{ textAlign: "right" }}>
                      {t("invoiceCreate.colUnitPrice")}
                    </span>
                    <span style={{ textAlign: "right" }}>
                      {t("invoiceCreate.colVatAmount")}
                    </span>
                    <span style={{ textAlign: "right" }}>
                      {t("invoiceCreate.colTotal")}
                    </span>
                    <span style={{ textAlign: "right" }}>
                      {t("invoiceCreate.colGross")}
                    </span>
                  </div>
                  {lines.length === 0 ? (
                    <div
                      style={{
                        padding: 24,
                        textAlign: "center",
                        color: R.textMuted,
                        fontSize: 13,
                      }}
                    >
                      {t("invoiceCreate.noLines")}
                    </div>
                  ) : (
                    lines.map((l, idx) => (
                      <InvoiceLineRow
                        key={l.id}
                        line={l}
                        index={idx}
                        amounts={breakdown.lines[idx]}
                        isLast={idx === lines.length - 1}
                        isSelected={selectedLineId === l.id}
                        onSelect={() => setSelectedLineId(l.id)}
                      />
                    ))
                  )}
                </div>
              </div>
              {/* Видимо (не само в tooltip) защо „Промяна"/„Изтриване" не
                  важат за реда за бакшиш. Само когато има такъв ред. */}
              {invoiceHasTipLines ? (
                <p className="m-0 text-[12px] font-medium leading-[1.4] text-pos-soft">
                  {t("invoiceTips.locked")}
                </p>
              ) : null}
              {/* Същото за реда „Приспаднато авансово плащане" (депозит). */}
              {invoiceHasDepositLines ? (
                <p className="m-0 text-[12px] font-medium leading-[1.4] text-pos-soft">
                  {t("depositInvoice.lockedDeduction")}
                </p>
              ) : null}
              {/* Обобщението — под редовете, в същата секция (до 0.0.389 —
                  своя): едно заглавие по-малко на височина. */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isNarrow ? "1fr" : "1fr 1fr",
                  gap: 14,
                  alignItems: "start",
                  marginTop: 2,
                  paddingTop: 10,
                  borderTop: `1px solid ${R.border}`,
                }}
              >
                {/* Ляво: словом + забележка */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                  }}
                >
                  <div>
                    <span
                      style={{
                        fontSize: 10.5,
                        fontWeight: 700,
                        color: R.textMuted,
                        letterSpacing: 1,
                        textTransform: "uppercase",
                      }}
                    >
                      {t("invoiceCreate.totalWords")}
                    </span>
                    <div
                      style={{
                        marginTop: 4,
                        padding: "8px 12px",
                        minHeight: 38,
                        background: R.bg,
                        border: `1px solid ${R.border}`,
                        borderRadius: 10,
                        fontSize: 13,
                        color: R.ink,
                        fontStyle: "italic",
                        lineHeight: 1.4,
                      }}
                    >
                      {moneyToWordsBg(breakdown.grandTotal)}
                    </div>
                  </div>
                  <div>
                    <span
                      style={{
                        fontSize: 10.5,
                        fontWeight: 700,
                        color: R.textMuted,
                        letterSpacing: 1,
                        textTransform: "uppercase",
                      }}
                    >
                      {t("invoiceCreate.note")}
                    </span>
                    <div style={{ marginTop: 4 }}>
                      <Input.TextArea
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        rows={1}
                        maxLength={500}
                        autoSize={{ minRows: 1, maxRows: 4 }}
                      />
                    </div>
                  </div>
                </div>

                {/* Дясно: 5 стойности (2x2 + последна на цяла ширина) */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: isNarrow ? "1fr 1fr" : "1fr 1fr",
                    gap: 8,
                  }}
                >
                  <TotalsRow
                    label={t("invoiceCreate.totalSum")}
                    value={breakdown.totalSum}
                  />
                  <TotalsRow
                    label={t("invoiceCreate.totalNonTaxable")}
                    value={breakdown.nonTaxable}
                  />
                  {breakdown.groups
                    .filter((g) => g.rate > 0)
                    .map((g) => (
                      <Fragment key={`vat-group-${g.rate}`}>
                        <TotalsRow
                          label={`${t("invoiceCreate.totalTaxable")} (${g.rate}%)`}
                          value={g.net}
                        />
                        <TotalsRow
                          label={`${t("invoiceCreate.totalVat")} (${g.rate}%)`}
                          value={g.vat}
                        />
                      </Fragment>
                    ))}
                  {/* Бакшишът — извън „Всичко сума" и ДДС, вътре в общата
                      стойност. Само при ред за бакшиш. */}
                  {breakdown.tipTotal > 0 ? (
                    <div className="col-span-full">
                      <TotalsRow
                        label={t("invoiceTips.totalTip")}
                        value={breakdown.tipTotal}
                      />
                    </div>
                  ) : null}
                  <div style={{ gridColumn: "1 / -1" }}>
                    <TotalsRow
                      label={t("invoiceCreate.totalGrand")}
                      value={breakdown.grandTotal}
                      emphasis
                    />
                  </div>
                </div>
              </div>
            </Section>
          </div>
        )}

        <ModalFooter isNarrow={isNarrow} narrowFirst>
          <GhostButton
            label={t("common.cancel")}
            onClick={onClose}
            shortcut={isDesktop ? "Esc" : undefined}
          />
          <PrimaryButton
            label={free ? t("freeInvoice.issue") : t("invoiceCreate.save")}
            onClick={handleSaveClick}
            disabled={lines.length === 0 || invoiceSubmit.isPending}
            loading={invoiceSubmit.isPending}
            shortcut={isDesktop ? "⏎" : undefined}
          />
        </ModalFooter>
      </div>

      {/* Item action modals — sibling-ове на основния модал */}
      <InvoiceLineEditModal
        open={editLineId !== null}
        mode="edit"
        initial={editLine}
        onClose={() => setEditLineId(null)}
        onSubmit={(data) => {
          if (editLineId)
            patchLine(editLineId, {
              name: data.name,
              unit: data.unit,
              vatRate: data.vatRate,
              quantity: data.quantity,
              unitPrice: data.unitPrice,
            });
          setEditLineId(null);
        }}
      />

      <InvoiceLineEditModal
        open={newLineOpen}
        mode="create"
        initial={null}
        onClose={() => setNewLineOpen(false)}
        onSubmit={(data) => {
          addLine(data);
          setNewLineOpen(false);
        }}
      />

      <InvoiceSummarizeModal
        open={summarizeOpen}
        currentMode={summarizeMode}
        hasTipLines={invoiceHasTipLines}
        onClose={() => setSummarizeOpen(false)}
        onConfirm={(nextMode) => {
          applySummarizeMode(nextMode);
          setSummarizeOpen(false);
        }}
      />

      <Modal
        open={deleteLineId !== null}
        onCancel={() => setDeleteLineId(null)}
        onOk={() => {
          if (deleteLineId) removeLine(deleteLineId);
          setDeleteLineId(null);
        }}
        okText={t("invoiceCreate.deleteConfirmOk")}
        cancelText={t("common.cancel")}
        okButtonProps={{ danger: true }}
        centered
        title={
          <span
            style={{ display: "inline-flex", gap: 8, alignItems: "center" }}
          >
            <ExclamationCircleFilled style={{ color: R.danger }} />
            {t("invoiceCreate.deleteConfirmTitle")}
          </span>
        }
      >
        <p style={{ margin: 0, color: R.textSoft, fontSize: 13 }}>
          {t("invoiceCreate.deleteConfirmDesc", {
            name: deleteLine?.name ?? "",
          })}
        </p>
      </Modal>

      <Modal
        open={mismatchConfirmOpen}
        onCancel={() => setMismatchConfirmOpen(false)}
        footer={null}
        centered
        closable={false}
        destroyOnHidden
        width={isMobile ? undefined : isTablet ? 460 : 520}
        styles={modalStyles}
        style={{ maxWidth: "calc(100vw - 32px)" }}
      >
        <div style={{ background: R.surface }}>
          <ModalHeader
            icon={<ExclamationCircleFilled />}
            title={t("invoiceCreate.mismatchTitle", {
              billsAmount: billsTotalLabel,
              context:
                bills.length > 1
                  ? t("invoiceCreate.mismatchManyBills")
                  : t("invoiceCreate.mismatchOneBill"),
            })}
            subtitle={t("invoiceCreate.mismatchBody")}
            onClose={() => setMismatchConfirmOpen(false)}
            isNarrow={isNarrow}
          />
          <div
            style={{
              padding: isNarrow ? "14px 16px" : "18px 24px",
              background: R.bg,
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 10,
            }}
          >
            <MismatchStat
              label={t("invoiceCreate.mismatchBillsTotal")}
              value={billsTotalLabel}
            />
            <MismatchStat
              label={t("invoiceCreate.mismatchInvoiceTotal")}
              value={breakdown.grandTotal.toFixed(2)}
              emphasis
            />
          </div>
          <ModalFooter isNarrow={isNarrow} narrowFirst>
            <GhostButton
              label={t("common.no")}
              onClick={() => setMismatchConfirmOpen(false)}
              shortcut={isDesktop ? "Esc" : undefined}
            />
            <PrimaryButton
              label={t("common.yes")}
              onClick={performSave}
              disabled={invoiceSubmit.isPending}
              loading={invoiceSubmit.isPending}
              shortcut={isDesktop ? "⏎" : undefined}
            />
          </ModalFooter>
        </div>
      </Modal>
    </Modal>
  );
};

function MismatchStat({
  label,
  value,
  emphasis,
}: {
  label: string;
  value: string;
  emphasis?: boolean;
}) {
  return (
    <div
      style={{
        padding: "10px 12px",
        background: emphasis ? R.brandTint : R.surface,
        border: `1px solid ${emphasis ? R.brandTint2 : R.border}`,
        borderRadius: 10,
      }}
    >
      <div
        style={{
          fontSize: 10.5,
          fontWeight: 700,
          color: emphasis ? R.brandDeep : R.textMuted,
          letterSpacing: 0.8,
          textTransform: "uppercase",
        }}
      >
        {label}
      </div>
      <div
        style={{
          marginTop: 4,
          fontFamily: R.mono,
          fontVariantNumeric: "tabular-nums",
          fontWeight: 700,
          fontSize: 16,
          color: emphasis ? R.brandDeep : R.ink,
        }}
      >
        {value} €
      </div>
    </div>
  );
}

// ── Layout primitives ──────────────────────────────────────────────────────

function Section({
  title,
  action,
  children,
}: {
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  // На narrow екран header-ът се качва над action-а вместо да е до него —
  // иначе action бутоните се притискат към малък ляв ръб (виж Артикули
  // секцията с 4 бутона) или влизат на 2 реда уж до title-а.
  const { isNarrow } = useViewport();
  return (
    <div
      style={{
        background: R.surface,
        border: `1px solid ${R.border}`,
        borderRadius: 12,
        padding: 12,
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: isNarrow && action ? "column" : "row",
          justifyContent: isNarrow && action ? "flex-start" : "space-between",
          alignItems: isNarrow && action ? "stretch" : "center",
          gap: isNarrow && action ? 8 : 0,
        }}
      >
        <span
          style={{
            fontSize: 10.5,
            fontWeight: 700,
            color: R.textMuted,
            letterSpacing: 1.2,
            textTransform: "uppercase",
          }}
        >
          {title}
        </span>
        {action}
      </div>
      {children}
    </div>
  );
}

function Field({
  label,
  hint,
  required,
  children,
}: {
  label: string;
  hint?: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      <span
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: R.textSoft,
          letterSpacing: 0.2,
        }}
      >
        {label}
        {required && <span style={{ color: R.danger, marginLeft: 3 }}>*</span>}
        {hint && (
          <span
            style={{
              marginLeft: 6,
              fontWeight: 500,
              color: R.textMuted,
            }}
          >
            ({hint})
          </span>
        )}
      </span>
      {children}
    </div>
  );
}

function TotalsRow({
  label,
  value,
  emphasis,
}: {
  label: string;
  value: number;
  emphasis?: boolean;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "7px 12px",
        background: emphasis ? R.brandTint : R.bg,
        border: `1px solid ${emphasis ? R.brandTint2 : R.border}`,
        borderRadius: 10,
      }}
    >
      <span
        style={{
          fontSize: 11,
          fontWeight: 700,
          color: emphasis ? R.brandDeep : R.textMuted,
          letterSpacing: 1,
          textTransform: "uppercase",
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: emphasis ? 15 : 13,
          fontWeight: 700,
          color: emphasis ? R.brandDeep : R.ink,
          fontFamily: R.mono,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {value.toFixed(2)}
      </span>
    </div>
  );
}

function SectionAction({
  icon,
  label,
  onClick,
  disabled,
}: {
  icon: React.ReactNode;
  label: string;
  onClick?: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        height: 30,
        padding: "0 10px",
        borderRadius: 8,
        border: `1px solid ${R.border}`,
        background: disabled ? R.bg : R.surface,
        color: disabled ? R.textMuted : R.brandDeep,
        cursor: disabled ? "not-allowed" : "pointer",
        fontSize: 12,
        fontWeight: 600,
        // display:flex (а не inline-flex) + justify center → бутонът се
        // разтегля да заеме grid cell-а ако той е 1fr, и остава
        // съдържание-широк ако cell-ът е auto.
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
        whiteSpace: "nowrap",
      }}
    >
      {icon}
      {label}
    </button>
  );
}

export default InvoiceCreateModal;
